'use client';

import { useRef, useState } from 'react';

import PackageCard from '@/features/healthPackages/components/PackageCard';
import type { CardLabels, PackageCardPresentation } from '@/types/packageCard';

import type { PackageCardContent } from './types';

const DESKTOP_COLUMN_CLASSES: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

interface PackageCardRowProps<T extends PackageCardContent> {
  cards: { card: T; index: number }[];
  columns?: 1 | 2 | 3 | 4;
  labels?: CardLabels;
  presentation?: PackageCardPresentation;
  onEnquire?: (card: T) => void;
  onBook?: (card: T) => void;
  canBook?: (card: T) => boolean;
}

export default function PackageCardRow<T extends PackageCardContent>({
  cards,
  columns = 3,
  labels,
  presentation,
  onEnquire,
  onBook,
  canBook,
}: PackageCardRowProps<T>) {
  const [openCardIds, setOpenCardIds] = useState<Set<number>>(() => {
    // Mirror `defaultOpen` so cards that start expanded also start the row in
    // `items-start`, exactly like the previous feature-specific rows.
    const initial = new Set<number>();
    cards.forEach(({ card, index }) => {
      if (card.defaultOpen) initial.add(index);
    });
    return initial;
  });
  const [frozenHeight, setFrozenHeight] = useState<number | null>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const handleCardToggle = (index: number, isOpen: boolean): void => {
    const wasAnyOpen = openCardIds.size > 0;

    if (!wasAnyOpen && isOpen) {
      // A card in this row is about to expand for the first time — snapshot
      // the row's current (uniformly stretched) height before the layout
      // changes, so the untouched siblings can be pinned to it instead of
      // shrinking down to their own natural size once stretch is dropped.
      const heights = cards
        .map(({ index: i }) => cardRefs.current.get(i)?.offsetHeight)
        .filter((h): h is number => typeof h === 'number');
      if (heights.length) setFrozenHeight(Math.max(...heights));
    }

    setOpenCardIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(index);
      else next.delete(index);
      if (next.size === 0) setFrozenHeight(null);
      return next;
    });
  };

  const rowHasOpen = openCardIds.size > 0;
  const bookAction = onBook;

  return (
    <div
      className={`grid grid-cols-1 gap-6 ${DESKTOP_COLUMN_CLASSES[columns]} ${rowHasOpen ? 'items-start' : 'items-stretch'}`}
    >
      {cards.map(({ card, index }) => {
        const isThisOpen = openCardIds.has(index);
        const shouldFreeze = rowHasOpen && !isThisOpen && frozenHeight !== null;
        // Booking is opt-in: an onBook callback alone is not enough. The
        // section predicate wins over the per-card flag.
        const shouldBook =
          Boolean(bookAction) && (canBook?.(card) ?? card.bookable ?? false);
        const cardPresentation: PackageCardPresentation = {
          ...presentation,
          ...card.presentation,
          showEnquireAction:
            card.presentation?.showEnquireAction ??
            presentation?.showEnquireAction ??
            Boolean(onEnquire),
        };
        const cardLabels =
          labels || card.labels ? { ...labels, ...card.labels } : undefined;

        return (
          <div
            key={card.id ?? index}
            ref={(el) => {
              if (el) cardRefs.current.set(index, el);
              else cardRefs.current.delete(index);
            }}
            className="break-inside-avoid pb-6"
            style={
              shouldFreeze
                ? { height: frozenHeight ?? undefined, overflow: 'hidden' }
                : undefined
            }
          >
            <PackageCard
              badge={card.badge}
              title={card.title}
              subtitle={card.subtitle}
              testType={card.testType}
              gender={card.gender}
              text4={card.text4}
              disclaimer={card.disclaimer}
              tests={card.tests}
              testGroups={card.testGroups}
              price={card.price}
              descriptionText={card.descriptionText}
              subDescription={card.subDescription}
              description={card.description}
              button={card.button}
              defaultOpen={card.defaultOpen}
              labels={cardLabels}
              presentation={cardPresentation}
              onToggleOpen={(isOpen) => handleCardToggle(index, isOpen)}
              onEnquire={onEnquire ? () => onEnquire(card) : undefined}
              onBook={
                shouldBook && bookAction
                  ? () => {
                      bookAction(card);
                    }
                  : undefined
              }
            />
          </div>
        );
      })}
    </div>
  );
}
